import { MAX_ATTACHMENT_SIZE } from '../../utils/attachmentLimits';
import React, { useState, useEffect, useRef } from 'react';
import heic2any from 'heic2any';
import { Paper, Typography, Box, InputLabel, MenuItem, FormControl, Select, TextField, Checkbox, FormControlLabel, Button, IconButton, Radio } from '@mui/material';
import Grid from '@material-ui/core/Grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ChildDevelopment from './ChildDevelopment';
import { Formik, Field, Form, useFormikContext } from 'formik';
import { FormHelperText } from '@material-ui/core';
import { medicalBackgroundValidationSchema } from 'validations/validationMedicalBackground';
import { notification } from 'antd';
import InstructionText from '../shared/InstructionText';

// Allowed image file extensions for upload
const ALLOWED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.hei', '.pdf'];
const MAX_FILE_SIZE = MAX_ATTACHMENT_SIZE; // 10MB limit

// [AC-FILEUPL0318-S1, E2] Requirement files upload constraints
// [OWASP:A05] Extension whitelist and per-file size cap enforced at selection time
const REQUIREMENT_ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heif', '.heic', '.pdf'];
const REQUIREMENT_MAX_FILE_SIZE = MAX_ATTACHMENT_SIZE; // 10MB

/**
 * Validates if the uploaded file has an allowed extension
 * @param {File} file - The file object to validate
 * @returns {boolean} - True if file extension is allowed, false otherwise
 */
const validateFileExtension = (file) => {
  if (!file) return false;
  
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  
  return ALLOWED_FILE_EXTENSIONS.includes(fileExtension);
};

function MedicalBackground({ handleNext, handleBack, onInformationChange, information, onSubmitApplication }) {
  const [currentMedicalBackgroundInformation, setCurrentMedicalBackgroundInformation] = useState(information);
  
  // Image preview state for HEIC conversion
  const [signatureImagePreview, setSignatureImagePreview] = useState(null);
  const [isConvertingSignature, setIsConvertingSignature] = useState(false);

  // [ISSUE-3 FIX] Blob URL cache for requirementFiles previews.
  // Prevents re-creating blob URLs on every render and leaking them on unmount.
  // [OWASP:A05] Ensures no accumulation of detached blob references in memory.
  const blobUrlCache = useRef(new Map());
  const getBlobUrl = (file) => {
    if (!blobUrlCache.current.has(file)) {
      blobUrlCache.current.set(file, URL.createObjectURL(file));
    }
    return blobUrlCache.current.get(file);
  };
  useEffect(() => {
    const cache = blobUrlCache.current;
    return () => {
      cache.forEach((url) => URL.revokeObjectURL(url));
      cache.clear();
    };
  }, []);

  // Update internal state when information prop changes (for draft restoration)
  useEffect(() => {
    setCurrentMedicalBackgroundInformation(information);
  }, [information]);

  const saveMedicalBackgroundInformation = (values) => {
    onInformationChange(values);
  }

  /**
   * Auto-scroll to first error field component
   * Detects first validation error and scrolls to it smoothly
   * Also shows notification when validation fails
   */
  const AutoScrollToError = () => {
    const { errors, touched, submitCount, isValidating, isSubmitting } = useFormikContext();
    const [lastSubmitCount, setLastSubmitCount] = useState(0);
    const [hasShownNotification, setHasShownNotification] = useState(false);

    useEffect(() => {
      // Track submit attempts
      if (submitCount > lastSubmitCount) {
        setLastSubmitCount(submitCount);
        setHasShownNotification(false);
      }
    }, [submitCount, lastSubmitCount]);

    useEffect(() => {
      // Show notification after validation completes with errors
      if (!isValidating && !isSubmitting && submitCount > 0 && !hasShownNotification) {
        if (errors && Object.keys(errors).length > 0) {
          const hasVisibleErrors = Object.keys(errors).some(key => touched[key]);
          
          if (hasVisibleErrors) {
            setHasShownNotification(true);
            
            // Show notification for incomplete form
            notification.error({
              message: "Form Incomplete",
              description: "Please fill in all required fields and correct any errors before proceeding.",
              duration: 4,
            });

            // Helper function to get nested field name from error path
            // Returns the first error field in visual order (as it appears on the page)
            const getFirstErrorFieldName = (errors, touched) => {
              // Define field order as they appear visually on the page
              const fieldOrder = [
                'isTherapyDone',
                'childTherapyStatus',
                'hasOtherTherapist',
                'therapistRecommendation',
                'signature', // This comes BEFORE Child Development section
                'requirementFiles',
                'childPlaymatesAge',
                'childSeparationIssues',
                'specificSeparationIssue',
                'childLanguagesUse',
                'specificChildLanguagesUse',
                'showTextFieldA',
                'childPhysicalChallenges',
                'childModeOfMobility',
                'showTextFieldB',
                'childSensesChallenges',
                'showTextFieldC',
                'childThinkingChallenges',
                'childUnderstandingChallenges',
                'showTextFieldD',
                'childBehaviorChallenges',
                'childBehaviorChallengesSpecify',
                'showTextFieldE',
                'childCommunicationChallenges',
              ];
              
              // Find the first field in visual order that has an error and is touched
              for (const fieldName of fieldOrder) {
                if (errors[fieldName] && touched[fieldName]) {
                  const error = errors[fieldName];
                  
                  // If it's an array of errors (like childTherapyStatus)
                  if (Array.isArray(error)) {
                    for (let i = 0; i < error.length; i++) {
                      if (error[i] && touched[fieldName] && touched[fieldName][i]) {
                        // Find first nested error field
                        const nestedError = error[i];
                        const nestedTouched = touched[fieldName][i];
                        
                        for (const nestedKey of Object.keys(nestedError)) {
                          if (nestedTouched[nestedKey]) {
                            return `${fieldName}[${i}].${nestedKey}`;
                          }
                        }
                      }
                    }
                  }
                  
                  // If it's a simple error, return the field name
                  return fieldName;
                }
              }
              
              return null;
            };

            const firstErrorFieldName = getFirstErrorFieldName(errors, touched);
            
            if (firstErrorFieldName) {
              // Try to find the field in the DOM with various selectors
              let errorElement = null;
              
              // Special handling for signature field
              if (firstErrorFieldName === 'signature') {
                // Look for the upload button or its label
                const uploadButton = document.querySelector('#raised-button-file1');
                const uploadLabel = document.querySelector('label[for="raised-button-file1"]');
                
                if (uploadLabel) {
                  errorElement = uploadLabel;
                } else if (uploadButton) {
                  errorElement = uploadButton;
                }
              }
              
              // General field search if not found yet
              if (!errorElement) {
                errorElement = 
                  document.querySelector(`[name="${firstErrorFieldName}"]`) ||
                  document.querySelector(`input[name="${firstErrorFieldName}"]`) ||
                  document.querySelector(`select[name="${firstErrorFieldName}"]`) ||
                  document.querySelector(`textarea[name="${firstErrorFieldName}"]`) ||
                  document.querySelector(`[id="${firstErrorFieldName}"]`) ||
                  document.querySelector(`[data-field="${firstErrorFieldName}"]`);
              }
              
              // If no direct element found, look for the error message itself
              // This handles checkbox/radio groups that don't have name attributes
              if (!errorElement) {
                // Find all FormHelperText elements with error messages
                const allHelperTexts = document.querySelectorAll('.MuiFormHelperText-root');
                for (const helperText of allHelperTexts) {
                  if (helperText.textContent && helperText.textContent.trim() !== '') {
                    // Check if this helper text is showing one of our errors
                    const errorMessage = errors[firstErrorFieldName];
                    if (errorMessage && helperText.textContent.includes(errorMessage)) {
                      errorElement = helperText;
                      break;
                    }
                  }
                }
              }
              
              // Final fallback: any visible error indicator
              if (!errorElement) {
                errorElement = 
                  document.querySelector('.Mui-error') ||
                  document.querySelector('[aria-invalid="true"]') ||
                  document.querySelector('.MuiFormHelperText-root');
              }
              
              if (errorElement) {
                // Scroll to error with smooth behavior - center vertically in viewport
                const elementRect = errorElement.getBoundingClientRect();
                const absoluteElementTop = elementRect.top + window.pageYOffset;
                const middle = absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2);
                
                window.scrollTo({ top: middle, behavior: 'smooth' });
                
                // Focus the element after scrolling if it's focusable
                setTimeout(() => {
                  if (errorElement.focus && typeof errorElement.focus === 'function') {
                    errorElement.focus({ preventScroll: true });
                  }
                }, 500);
              }
            }
          }
        }
      }
    }, [isValidating, isSubmitting, errors, touched, submitCount, hasShownNotification]);

    return null;
  };

  return (
    <Formik
      initialValues={currentMedicalBackgroundInformation}
      validationSchema={medicalBackgroundValidationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          saveMedicalBackgroundInformation(values);
          await onSubmitApplication(values);
        } catch (error) {
          console.error('Error submitting medical background form:', error);
          notification.error({
            message: 'Error',
            description: 'An error occurred while submitting. Please try again.',
          });
        } finally {
          setSubmitting(false);
        }
      }}
      enableReinitialize
    >
      {({ errors, touched, values, setFieldValue, handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit} encType="multipart/form-data">
          <AutoScrollToError />
          <Paper sx={{ mt: 2 }}>
            <Box p={3}>
              <InstructionText />
              <Box mb={2}>
                <Typography fontWeight="bold" mb={1}>
                  I. Medical Background
                </Typography>
                <Typography>
                  1. Nakapag therapy na ba ang inyong anak? <span style={{color: 'red'}}>*</span>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Grid item xs={12} md={3} style={{ marginBottom: '16px' }}>
                      <FormControl fullWidth size="small">
                        <Field
                          as={Select}
                          name="isTherapyDone"
                          helperText={touched?.isTherapyDone && errors?.isTherapyDone}
                          error={touched?.isTherapyDone && errors?.isTherapyDone}
                        >
                          <MenuItem value="yes">Oo</MenuItem>
                          <MenuItem value="no">Hindi</MenuItem>
                        </Field>
                        <FormHelperText>{touched?.isTherapyDone && errors?.isTherapyDone}</FormHelperText>
                      </FormControl>
                    </Grid>
                    {values.isTherapyDone == 'yes' ?
                      <>
                      <Typography mb={2} variant="body2" color="textSecondary">
                        Kung oo, anong klaseng therapy at gaano katagal na ito? <span style={{color: 'red'}}>*</span>
                      </Typography>
                        {values.childTherapyStatus.map((row, index) => (
                          <Grid container spacing={2} wrap="nowrap" style={{ marginBottom: '8px' }}>
                            <Grid item md={4}>
                              <Field
                                as={TextField}
                                name={`childTherapyStatus[${index}].name`}
                                size="small"
                                label="Therapy (PT, OT, SLP)"
                                value={row.name}
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                fullWidth
                                helperText={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].name && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].name}
                                error={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].name && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].name}
                              />
                            </Grid>
                            <Grid item md={4}>
                              <Field
                                as={TextField}
                                name={`childTherapyStatus[${index}].duration`}
                                size="small"
                                label="Duration / Month and Year"
                                value={row.duration}
                                variant="outlined"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                fullWidth
                                helperText={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].duration && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].duration}
                                error={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].duration && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].duration}
                              />
                            </Grid>
                            <Grid item md={4}>
                              <Box display="flex" alignItems="center">
                                <Field
                                  as={TextField}
                                  name={`childTherapyStatus[${index}].goal`}
                                  size="small"
                                  label="Mga Layunin ng Therapy"
                                  value={row.goal}
                                  variant="outlined"
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  fullWidth
                                  helperText={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].goal && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].goal}
                                  error={touched.childTherapyStatus && touched.childTherapyStatus[index] && touched.childTherapyStatus[index].goal && errors.childTherapyStatus && errors.childTherapyStatus[index] && errors.childTherapyStatus[index].goal}
                                />
                                <Button
                                  aria-label="delete"
                                  onClick={() => {
                                    const updatedChildTherapyStatus = values.childTherapyStatus.filter((_, i) => i !== index);
                                    setFieldValue("childTherapyStatus", updatedChildTherapyStatus);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        ))
                        }
                        <Button variant="contained"
                          onClick={() => {
                            const newField = [...values.childTherapyStatus,
                            {
                              name: '',
                              duration: '',
                              therapyGoal: '',
                            }];
                            setFieldValue("childTherapyStatus", newField);
                          }}
                        >
                          <AddIcon fontSize="small" />
                          Magdagdag
                        </Button>
                      </>
                      : ''}
                  </Grid>
                </Grid>
              </Box>
              <Box mb={3}>
                <Typography mb={1}>
                  2. Mayroon bang ibang espesyalistang tumitingin sa inyong anak sa kasalukuyan? <span style={{color: 'red'}}>*</span>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <FormControl fullWidth size="small">
                          <Field
                            as={Select}
                            name="hasOtherTherapist"
                            helperText={touched.hasOtherTherapist && errors.hasOtherTherapist}
                            error={touched.hasOtherTherapist && errors.hasOtherTherapist}
                          >
                            <MenuItem value="yes">Oo</MenuItem>
                            <MenuItem value="no">Hindi</MenuItem>
                          </Field>
                          <FormHelperText>{touched.hasOtherTherapist && errors.hasOtherTherapist}</FormHelperText>
                        </FormControl>
                      </Grid>
                      {values.hasOtherTherapist == 'yes' ?
                        <Grid item xs={12} md={12}>
                          <Field
                            as={TextField}
                            name="therapistRecommendation"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            label={<>Espesyalista ito ng ano at ano ang recommendasyon <span style={{color: 'red'}}>*</span></>}
                            size="small"
                            fullWidth
                            helperText={touched.therapistRecommendation && errors.therapistRecommendation}
                            error={errors.therapistRecommendation && touched.therapistRecommendation}
                          />
                        </Grid> : null
                      }
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
              <Box mb={2}>
                <Typography mb={1}>
                  3. Signature ng magulang <span style={{color: 'red'}}>*</span>
                </Typography>
                {values.signature &&
                  <Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        {isConvertingSignature ? (
                          <Box
                            sx={{
                              width: "100%",
                              marginTop: "10px",
                              padding: "20px",
                              textAlign: "center",
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              Converting image for preview...
                            </Typography>
                          </Box>
                        ) : (
                          <img 
                            src={signatureImagePreview || URL.createObjectURL(values.signature)} 
                            alt="Parent Signature" 
                            style={{ width: "100%", marginTop: '10px' }}
                            onError={(e) => {
                              console.error('Image preview error:', e);
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                }
                <input
                  accept=".jpg,.jpeg,.png,.heif,.heic,.hei,.webp"
                  style={{ display: 'none' }}
                  id="raised-button-file1"
                  name="signature"
                  type="file"
                  onChange={async (event) => {
                    const file = event.currentTarget.files[0];
                    
                    if (file) {
                      if (file.size > MAX_FILE_SIZE) {
                        notification.error({
                          message: 'File Too Large',
                          description: file.name + ': Please choose a file that is 10MB or smaller.',
                          duration: 5,
                        });
                        event.currentTarget.value = '';
                        setFieldValue('signature', null);
                        setSignatureImagePreview(null);
                        return;
                      }
                      // Validate file extension
                      if (!validateFileExtension(file)) {
                        notification.error({
                          message: 'Invalid File Type',
                          description: 'Please upload only image files (.jpg, .jpeg, .png, .heif, .heic, .hei, .webp)',
                          duration: 5
                        });
                        // Clear the input
                        event.currentTarget.value = '';
                        setFieldValue('signature', null);
                        setSignatureImagePreview(null);
                        return;
                      }
                      
                      // Check if file is HEIC/HEIF/WEBP and convert it
                      const isHeicOrWebp = file.name.toLowerCase().match(/\.(heic|heif|hei|webp)$/);
                      
                      if (isHeicOrWebp) {
                        try {
                          setIsConvertingSignature(true);
                          
                          // Convert HEIC/HEIF/WEBP to JPEG
                          const convertedBlob = await heic2any({
                            blob: file,
                            toType: "image/jpeg",
                            quality: 0.9
                          });
                          
                          // Create preview URL from converted blob
                          const previewUrl = URL.createObjectURL(convertedBlob);
                          setSignatureImagePreview(previewUrl);
                          
                          // Clean up previous preview URL if exists
                          return () => {
                            if (previewUrl) {
                              URL.revokeObjectURL(previewUrl);
                            }
                          };
                          
                          setIsConvertingSignature(false);
                          
                          notification.success({
                            message: isHeicOrWebp[0].endsWith('webp') ? 'WEBP Image Converted' : 'HEIC Image Converted',
                            description: `Your ${isHeicOrWebp[0].toUpperCase().replace('.', '')} image has been converted and is ready for preview.`,
                            duration: 3
                          });
                        } catch (error) {
                          console.error('Image conversion error:', error);
                          setIsConvertingSignature(false);
                          setSignatureImagePreview(null);
                          
                          notification.warning({
                            message: 'Preview Not Available',
                            description: 'Could not convert image for preview, but the file will still be uploaded.',
                            duration: 4
                          });
                        }
                      } else {
                        // For other images, clear any previous preview
                        setSignatureImagePreview(null);
                      }
                      
                      setFieldValue('signature', file);
                    }
                  }}
                />
                <label htmlFor="raised-button-file1">
                  <Button style={{ marginTop: values.signature ? '20px' : '0' }} variant="contained" component="span">
                    Upload
                  </Button>
                </label>
                {errors.signature && touched.signature && (
                  <FormHelperText error>{errors.signature}</FormHelperText>
                )}
              </Box>
              {/* 4. File Uploads — [AC-FILEUPL0318-F1, F2, F3, E3] */}
              <Box mb={2}>
                <Typography mb={1}>4. File Uploads</Typography>
                {/* Preview list — [AC-FILEUPL0318-F3] */}
                {values.requirementFiles && values.requirementFiles.map((file, index) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    {file.type === 'application/pdf' ? (
                      <Typography variant="body2">{file.name}</Typography>
                    ) : (
                      <img
                        src={getBlobUrl(file)}
                        alt={file.name}
                        style={{ width: 60, height: 60, objectFit: 'cover', marginRight: 8 }}
                      />
                    )}
                    {file.converting ? (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        data-testid={`requirement-file-converting-${index}`}
                      >
                        Converting...
                      </Typography>
                    ) : null}
                    <IconButton
                      aria-label={`Remove file ${file.name}`}
                      size="small"
                      onClick={() => {
                        const updated = values.requirementFiles.filter((_, i) => i !== index);
                        setFieldValue('requirementFiles', updated);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {/* Add File input — [AC-FILEUPL0318-F2] */}
                <input
                  data-testid="requirement-files-input"
                  accept=".jpg,.jpeg,.png,.heif,.heic,.pdf"
                  style={{ display: 'none' }}
                  id="requirement-files-input"
                  type="file"
                  multiple
                  onChange={async (event) => {
                    // [AC-FILEUPL0318-P1, S1, E2] onChange handler
                    // [OWASP:A05] Extension whitelist + per-file size guard enforced at selection time
                    const inputEl = event.currentTarget;
                    const files = Array.from(inputEl.files || []);
                    let accumulated = [...(values.requirementFiles || [])];

                    for (const file of files) {
                      // [A05] Extension guard
                      const nameLC = file.name.toLowerCase();
                      const ext = nameLC.substring(nameLC.lastIndexOf('.'));
                      if (!REQUIREMENT_ALLOWED_EXTENSIONS.includes(ext)) {
                        notification.error({
                          message: 'Invalid File Type',
                          description: `${file.name}: Only .jpg, .jpeg, .png, .heif, .heic, .pdf files are allowed.`,
                          duration: 5,
                        });
                        continue;
                      }
                      // [A05] Per-file size guard
                      if (file.size > REQUIREMENT_MAX_FILE_SIZE) {
                        notification.error({
                          message: 'File Too Large',
                          description: `${file.name}: Each file must be smaller than 10MB.`,
                          duration: 5,
                        });
                        continue;
                      }

                      const isHeic = ext === '.heic' || ext === '.heif';
                      if (isHeic) {
                        // Add placeholder immediately so Converting indicator renders
                        const placeholder = { name: file.name, type: 'converting', converting: true, size: file.size };
                        accumulated = [...accumulated, placeholder];
                        setFieldValue('requirementFiles', accumulated);
                        try {
                          const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
                          const jpegFile = new File(
                            [convertedBlob],
                            file.name.replace(/\.(heic|heif)$/i, '.jpg'),
                            { type: 'image/jpeg' }
                          );
                          accumulated = accumulated.filter((f) => f !== placeholder).concat(jpegFile);
                          setFieldValue('requirementFiles', accumulated);
                        } catch (_err) {
                          accumulated = accumulated.filter((f) => f !== placeholder);
                          setFieldValue('requirementFiles', accumulated);
                          notification.warning({
                            message: 'Conversion Failed',
                            description: `Could not convert ${file.name}. File not added.`,
                            duration: 4,
                          });
                        }
                      } else {
                        accumulated = [...accumulated, file];
                      }
                    }

                    // Final state update for non-HEIC files accumulated in loop
                    setFieldValue('requirementFiles', accumulated);
                    if (inputEl) inputEl.value = '';
                  }}
                />
                <Button
                  variant="contained"
                  disabled={values.requirementFiles && values.requirementFiles.length >= 5}
                  onClick={() => {
                    const input = document.getElementById('requirement-files-input');
                    if (input) input.click();
                  }}
                >
                  Add File
                </Button>
                {errors.requirementFiles && touched.requirementFiles && (
                  <FormHelperText error>
                    {typeof errors.requirementFiles === 'string'
                      ? errors.requirementFiles
                      : 'Invalid file(s) in the upload list.'}
                  </FormHelperText>
                )}
              </Box>
              <ChildDevelopment
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                values={values}
              />
            </Box>
          </Paper>
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 2,
            gap: 2,
            px: { xs: 1, sm: 0 }
          }}>
            <Button
              color="inherit"
              onClick={() => {
                saveMedicalBackgroundInformation(values);
                handleBack();
              }}
              variant="outlined"
              sx={{ 
                width: { xs: 'auto', sm: 'auto' }
              }}
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              color="primary"
              sx={{ 
                width: { xs: 'auto', sm: 'auto' }
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default MedicalBackground;
